import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, Blog, Tag } from '@/lib/supabase';
import { generateSlug } from '@/lib/utils';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from '@/components/ui/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { 
  Save, 
  ArrowLeft, 
  UploadCloud, 
  Trash2, 
  Image as ImageIcon,
  Tag as TagIcon,
  X,
  Plus,
  LinkIcon
} from 'lucide-react';

interface FormData {
  title: string;
  slug: string;
  summary: string;
  content: string;
  cover_image: string;
  published: boolean;
}

const BlogEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<FormData>({
    title: '',
    slug: '',
    summary: '',
    content: '',
    cover_image: '',
    published: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);
  
  const isEditing = !!id;
  
  // Fetch blog data if editing
  useEffect(() => {
    if (isEditing) {
      fetchBlogData();
    }
    fetchTags();
  }, [id]);
  
  // Generate slug from title
  useEffect(() => {
    if (autoGenerateSlug && formData.title) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(prev.title),
      }));
    }
  }, [formData.title, autoGenerateSlug]);
  
  const fetchBlogData = async () => {
    try {
      setLoading(true);
      
      // Fetch blog
      const { data: blogData, error: blogError } = await supabase
        .from('blogs')
        .select('*')
        .eq('id', id)
        .single();
        
      if (blogError) throw blogError;
      
      if (blogData) {
        setFormData({
          title: blogData.title,
          slug: blogData.slug,
          summary: blogData.summary,
          content: blogData.content,
          cover_image: blogData.cover_image,
          published: blogData.published,
        });
        
        setImagePreview(blogData.cover_image);
        setAutoGenerateSlug(false);
        
        // Fetch blog tags
        const { data: blogTags, error: tagsError } = await supabase
          .from('blog_tags')
          .select('tag_id')
          .eq('blog_id', id);
          
        if (tagsError) throw tagsError;
        
        if (blogTags) {
          setSelectedTags(blogTags.map(tag => tag.tag_id));
        }
      }
    } catch (err) {
      console.error('Error fetching blog data:', err);
      toast({
        title: "Error",
        description: "Failed to load blog data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setAllTags(data || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
      toast({
        title: "Error",
        description: "Failed to load tags. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'slug') {
      setAutoGenerateSlug(false);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handlePublishedChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      published: checked,
    }));
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image is too large. Maximum size is 5MB.",
        variant: "destructive",
      });
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Selected file is not an image.",
        variant: "destructive",
      });
      return;
    }
    
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  const uploadImage = async (): Promise<string> => {
    if (!imageFile) {
      return formData.cover_image; // Return existing image if no new image
    }
    
    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`;
    
    try {
      // Create a simulated progress indicator
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + 10;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 300);
      
      const { error } = await supabase.storage
        .from('blog_images')
        .upload(filePath, imageFile);
        
      clearInterval(progressInterval);
      
      if (error) throw error;
      
      setUploadProgress(100);
      
      // Get public URL
      const { data } = supabase.storage
        .from('blog_images')
        .getPublicUrl(filePath);
        
      return data.publicUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };
  
  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    
    try {
      // Check if tag already exists
      let tagId = '';
      const existingTag = allTags.find(tag => tag.name.toLowerCase() === newTag.trim().toLowerCase());
      
      if (existingTag) {
        tagId = existingTag.id;
      } else {
        // Create new tag
        const { data, error } = await supabase
          .from('tags')
          .insert([{ name: newTag.trim() }])
          .select()
          .single();
          
        if (error) throw error;
        
        tagId = data.id;
        
        // Update allTags state
        setAllTags([...allTags, data]);
      }
      
      // Add to selected tags if not already selected
      if (!selectedTags.includes(tagId)) {
        setSelectedTags([...selectedTags, tagId]);
      }
      
      setNewTag('');
    } catch (err) {
      console.error('Error adding tag:', err);
      toast({
        title: "Error",
        description: "Failed to add tag. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleRemoveTag = (tagId: string) => {
    setSelectedTags(selectedTags.filter(id => id !== tagId));
  };
  
  const handleSelectTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      handleRemoveTag(tagId);
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "Please enter a title for the blog post.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.slug.trim()) {
      toast({
        title: "Error",
        description: "Please enter a slug for the blog post.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.summary.trim()) {
      toast({
        title: "Error",
        description: "Please enter a summary for the blog post.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.content.trim()) {
      toast({
        title: "Error",
        description: "Please enter content for the blog post.",
        variant: "destructive",
      });
      return;
    }
    
    if (!imagePreview && !formData.cover_image) {
      toast({
        title: "Error",
        description: "Please upload a cover image for the blog post.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSaveLoading(true);
      
      // Upload image if changed
      let coverImageUrl = formData.cover_image;
      if (imageFile) {
        coverImageUrl = await uploadImage();
      }
      
      const now = new Date().toISOString();
      const blogData = {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        summary: formData.summary.trim(),
        content: formData.content.trim(),
        cover_image: coverImageUrl,
        published: formData.published,
        updated_at: now,
      };
      
      if (isEditing) {
        // Update existing blog
        const { error } = await supabase
          .from('blogs')
          .update(blogData)
          .eq('id', id);
          
        if (error) throw error;
        
        // Delete existing blog-tag relations
        const { error: deleteError } = await supabase
          .from('blog_tags')
          .delete()
          .eq('blog_id', id);
          
        if (deleteError) throw deleteError;
        
        // Insert new blog-tag relations
        if (selectedTags.length > 0) {
          const blogTags = selectedTags.map(tagId => ({
            blog_id: id,
            tag_id: tagId,
          }));
          
          const { error: tagError } = await supabase
            .from('blog_tags')
            .insert(blogTags);
            
          if (tagError) throw tagError;
        }
        
        toast({
          title: "Success",
          description: "Blog post updated successfully.",
        });
      } else {
        // Create new blog
        const { data: newBlog, error } = await supabase
          .from('blogs')
          .insert([{
            ...blogData,
            published_at: formData.published ? now : null,
            created_at: now,
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        // Insert blog-tag relations
        if (selectedTags.length > 0) {
          const blogTags = selectedTags.map(tagId => ({
            blog_id: newBlog.id,
            tag_id: tagId,
          }));
          
          const { error: tagError } = await supabase
            .from('blog_tags')
            .insert(blogTags);
            
          if (tagError) throw tagError;
        }
        
        toast({
          title: "Success",
          description: "Blog post created successfully.",
        });
      }
      
      // Navigate back to blogs list
      navigate('/admin/blogs');
    } catch (err) {
      console.error('Error saving blog:', err);
      toast({
        title: "Error",
        description: "Failed to save blog post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoading(false);
      setUploadProgress(0);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout 
        title={isEditing ? "Edit Blog Post" : "Create Blog Post"}
        subtitle={isEditing ? "Update your blog content" : "Add new content to your blog"}
      >
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout 
      title={isEditing ? "Edit Blog Post" : "Create Blog Post"}
      subtitle={isEditing ? "Update your blog content" : "Add new content to your blog"}
    >
      <div className="mb-6 flex items-center">
        <Button 
          variant="outline" 
          onClick={() => navigate('/admin/blogs')}
          className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-200"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blogs
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content column */}
          <div className="md:col-span-2 space-y-6">
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-white">Blog Details</CardTitle>
                <CardDescription className="text-gray-400">
                  Basic information about your blog post
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-200">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter a title for your blog post"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="slug" className="text-gray-200">URL Slug</Label>
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="auto-slug" className="text-xs text-gray-400">Auto generate</Label>
                      <Switch 
                        id="auto-slug" 
                        checked={autoGenerateSlug} 
                        onCheckedChange={setAutoGenerateSlug}
                      />
                    </div>
                  </div>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-600 bg-gray-700 text-gray-400 text-sm">
                      /blog/
                    </span>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="url-friendly-slug"
                      className="rounded-l-none bg-gray-700 border-gray-600 text-white"
                      disabled={autoGenerateSlug}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="summary" className="text-gray-200">Summary</Label>
                  <Textarea
                    id="summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleInputChange}
                    placeholder="Brief summary of your blog post"
                    className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content" className="text-gray-200">Content</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Write your blog post content here..."
                    className="bg-gray-700 border-gray-600 text-white min-h-[300px]"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar column */}
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-white">Publishing</CardTitle>
                <CardDescription className="text-gray-400">
                  Control when your post goes live
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="published" className="text-gray-200">Published Status</Label>
                  <Switch 
                    id="published" 
                    checked={formData.published} 
                    onCheckedChange={handlePublishedChange} 
                  />
                </div>
                <p className="text-sm text-gray-400">
                  {formData.published 
                    ? "This post will be visible to visitors." 
                    : "This post is a draft and will not be visible to visitors."}
                </p>
              </CardContent>
              <CardFooter>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600"
                  disabled={saveLoading}
                >
                  {saveLoading ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isEditing ? 'Update Blog Post' : 'Publish Blog Post'}
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-white">Cover Image</CardTitle>
                <CardDescription className="text-gray-400">
                  Upload a featured image for your post
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Cover preview" 
                      className="w-full h-40 object-cover rounded-md"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 w-8 h-8 p-0 rounded-full"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        if (!isEditing) {
                          setFormData(prev => ({ ...prev, cover_image: '' }));
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-gray-600 rounded-md p-6 text-center hover:bg-gray-700/30 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-400">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                )}
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  className="hidden"
                  accept="image/*"
                />
                
                {uploadProgress > 0 && (
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div 
                      className="bg-teal-500 h-2.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-white">Tags</CardTitle>
                <CardDescription className="text-gray-400">
                  Categorize your blog post
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Input
                    placeholder="Add a new tag..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddTag}
                    className="bg-teal-500 hover:bg-teal-600"
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-200">Available Tags</Label>
                  <Select
                    onValueChange={handleSelectTag}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select a tag" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {allTags.map(tag => (
                        <SelectItem 
                          key={tag.id} 
                          value={tag.id}
                          className={selectedTags.includes(tag.id) ? "text-teal-400" : ""}
                        >
                          {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedTags.map(tagId => {
                    const tag = allTags.find(t => t.id === tagId);
                    return tag ? (
                      <div 
                        key={tag.id}
                        className="flex items-center bg-gray-700 px-3 py-1 rounded-full text-sm text-teal-400"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag.id)}
                          className="ml-2 text-gray-400 hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default BlogEdit; 